import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import {
  RideWiseRow,
  TaxiCallerRow,
  CrmAccountRow,
  CrmApprovalRow,
  CrmPermanentBookingRow,
  DriverRow,
  VehicleRow,
  FleetPulseFilters,
} from "./types";
import { safeArr } from "./helpers";

const PAGE = 1000;

async function fetchAllPaginated<T>(builder: () => any): Promise<T[]> {
  const results: T[] = [];
  let from = 0;
  // hard-cap to avoid runaway loops
  for (let i = 0; i < 200; i++) {
    const { data, error } = await builder().range(from, from + PAGE - 1);
    if (error) throw error;
    const rows = safeArr<T>(data as T[]);
    results.push(...rows);
    if (rows.length < PAGE) break;
    from += PAGE;
  }
  return results;
}

export interface FleetPulseDataState {
  loading: boolean;
  error: string | null;
  ridewise: RideWiseRow[];
  taxicaller: TaxiCallerRow[];
  accounts: CrmAccountRow[];
  approvals: CrmApprovalRow[];
  permanentBookings: CrmPermanentBookingRow[];
  drivers: DriverRow[];
  vehicles: VehicleRow[];
  refetch: () => void;
}

/** Loads everything Fleet Pulse needs, scoped by date range only.
 *  Other filters (vehicle/driver/region/hoist/scope) are applied in-memory
 *  in the `useFilteredFleetPulseData` hook so that filters react instantly.
 */
export function useFleetPulseData(
  startDate: string,
  endDate: string
): FleetPulseDataState {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const [ridewise, setRidewise] = useState<RideWiseRow[]>([]);
  const [taxicaller, setTaxicaller] = useState<TaxiCallerRow[]>([]);
  const [accounts, setAccounts] = useState<CrmAccountRow[]>([]);
  const [approvals, setApprovals] = useState<CrmApprovalRow[]>([]);
  const [permanentBookings, setPermanentBookings] = useState<
    CrmPermanentBookingRow[]
  >([]);
  const [drivers, setDrivers] = useState<DriverRow[]>([]);
  const [vehicles, setVehicles] = useState<VehicleRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const rwCols =
          "id,trip_date,driver_number,vehicle_identifier,region,fare_paid,fare_total,concession_amount,number_of_passengers,kilometres,hoist_deployed,client_number,client_surname,client_first_name,taxicaller_job_id,start_time,end_time";

        const tcCols =
          'id,pu_date,"Cost_Code","Account_ID","Project","Reference",vehicle_number,driver_name,trip_total,grand_total,job_km,"Passenger_First_Name","Passenger_Last_Name","Job_ID",driver_completed';

        const [
          rwData,
          tcData,
          acctRes,
          apprvRes,
          pbRes,
          drvRes,
          vehRes,
        ] = await Promise.all([
          fetchAllPaginated<RideWiseRow>(() =>
            supabase
              .from("ridewise_data")
              .select(rwCols)
              .gte("trip_date", startDate)
              .lte("trip_date", endDate)
              .order("trip_date", { ascending: true })
          ),
          fetchAllPaginated<TaxiCallerRow>(() =>
            supabase
              .from("taxicaller_data")
              .select(tcCols)
              .gte("pu_date", startDate)
              .lte("pu_date", endDate)
          ),
          supabase
            .from("crm_accounts")
            .select(
              "id,account_id,account_name,default_cost_code,is_active,traveller_full_name"
            ),
          supabase
            .from("crm_account_approvals")
            .select(
              "id,account_id,cost_code,approval_from,approval_to,is_current,is_active,approval_is_open"
            ),
          supabase
            .from("crm_permanent_bookings")
            .select("id,account_id,is_active"),
          supabase
            .from("drivers")
            .select(
              "ridewise_id,driver_name,driver_firstname,driver_lastname,is_active"
            ),
          supabase
            .from("vehicles")
            .select("vehicle_number,vehicle_type,status,registration"),

        ]);

        if (cancelled) return;

        if (acctRes.error) throw acctRes.error;
        if (apprvRes.error) throw apprvRes.error;
        if (pbRes.error) throw pbRes.error;
        if (drvRes.error) throw drvRes.error;
        if (vehRes.error) throw vehRes.error;

        setRidewise(safeArr(rwData));
        setTaxicaller(safeArr(tcData));
        setAccounts(safeArr(acctRes.data as CrmAccountRow[]));
        setApprovals(safeArr(apprvRes.data as CrmApprovalRow[]));
        setPermanentBookings(
          safeArr(pbRes.data as CrmPermanentBookingRow[])
        );
        setDrivers(safeArr(drvRes.data as DriverRow[]));
        setVehicles(safeArr(vehRes.data as VehicleRow[]));
      } catch (e: any) {
        if (cancelled) return;
        console.error("Fleet Pulse data load failed", e);
        setError(e?.message || "Failed to load data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, tick]);

  return {
    loading,
    error,
    ridewise,
    taxicaller,
    accounts,
    approvals,
    permanentBookings,
    drivers,
    vehicles,
    refetch: () => setTick((t) => t + 1),
  };
}

/** Apply in-memory filters (vehicle / driver / region / hoist / scope / month-year). */
export function useFilteredFleetPulseData(
  base: FleetPulseDataState,
  f: FleetPulseFilters
) {
  return useMemo(() => {
    const driversById = new Map<string, DriverRow>();
    for (const d of safeArr(base.drivers)) {
      if (d.ridewise_id) driversById.set(String(d.ridewise_id), d);
    }
    const vehiclesById = new Map<string, VehicleRow>();
    for (const v of safeArr(base.vehicles)) {
      if (v.vehicle_number) vehiclesById.set(String(v.vehicle_number), v);
    }

    let rw = safeArr(base.ridewise);

    if (f.vehicleId !== "all") {
      rw = rw.filter((r) => (r.vehicle_identifier || "") === f.vehicleId);
    }
    if (f.driverId !== "all") {
      rw = rw.filter((r) => (r.driver_number || "") === f.driverId);
    }
    if (f.region !== "all") {
      rw = rw.filter((r) => (r.region || "") === f.region);
    }
    if (f.hoistOnly) {
      rw = rw.filter((r) => !!r.hoist_deployed);
    }
    if (f.month !== "all") {
      rw = rw.filter((r) => (r.trip_date || "").slice(5, 7) === f.month);
    }
    if (f.year !== "all") {
      rw = rw.filter((r) => (r.trip_date || "").slice(0, 4) === f.year);
    }

    if (f.fleetScope !== "all") {
      rw = rw.filter((r) => {
        const veh = vehiclesById.get(String(r.vehicle_identifier || ""));
        const isCurrent =
          !!veh &&
          (veh.status || "").toLowerCase() !== "inactive" &&
          (veh.status || "").toLowerCase() !== "historical";
        return f.fleetScope === "current" ? isCurrent : !isCurrent;
      });
    }

    if (f.driverScope !== "all") {
      rw = rw.filter((r) => {
        const drv = driversById.get(String(r.driver_number || ""));
        const isCurrent = !!drv && drv.is_active === true;
        return f.driverScope === "current" ? isCurrent : !isCurrent;
      });
    }

    let tc = safeArr(base.taxicaller);
    if (f.month !== "all") {
      tc = tc.filter((r) => (r.pu_date || "").slice(5, 7) === f.month);
    }
    if (f.year !== "all") {
      tc = tc.filter((r) => (r.pu_date || "").slice(0, 4) === f.year);
    }

    return { rw, tc, driversById, vehiclesById };
  }, [base, f]);
}
